// What a new-mail push shows (docs/WEBMAIL.md, phase 5). After archiving new mail the server's sync timer sends one
// push per device (yahoomail/push.py): the newest messages still unread in the folders chosen for notifications and
// the unread count of those folders for the icon badge. The service worker (sw.ts) turns it into notifications with
// the functions here, which import nothing so the worker stays small.

export interface PushMessage {
  id: string;
  folder: string;
  from: string | null;
  addr: string | null;
  subject: string;
  snippet: string;
  date: string | null;
}

export interface PushPayload {
  v: number;
  /** Unread mail in the chosen folders: the icon badge. */
  unread: number;
  messages: PushMessage[];
  /** New messages left out of the push (it holds at most five). */
  more: number;
  /** A test push from the Notifications screen. */
  test?: boolean;
}

export interface ShownNotification {
  title: string;
  options: NotificationOptions & { tag: string; data: { url: string } };
}

/** Up to this many new messages get a notification each; more come as one. */
export const SEPARATE_MAX = 3;
export const ICON = "/icon-192.png";
/** The small monochrome icon Android shows in the status bar. */
export const BADGE = "/badge-96.png";

/** The app's address of a message in a folder (as route.ts writes it: /?f=folder&m=id, Inbox left out). */
export function messageUrl(folder: string, id: string): string {
  const p = new URLSearchParams();
  if (folder !== "Inbox") p.set("f", folder);
  p.set("m", id);
  return `/?${p}`;
}

export function folderUrl(folder: string): string {
  return folder === "Inbox" ? "/" : `/?${new URLSearchParams({ f: folder })}`;
}

/** The push's JSON, or null when it is not one of ours. */
export function parsePayload(text: string): PushPayload | null {
  try {
    const data = JSON.parse(text) as Partial<PushPayload>;
    if (typeof data !== "object" || data === null || !Array.isArray(data.messages)) return null;
    return {
      v: Number(data.v) || 1,
      unread: Math.max(0, Number(data.unread) || 0),
      messages: data.messages.filter((m): m is PushMessage => typeof m === "object" && m !== null && typeof m.id === "string" && typeof m.folder === "string"),
      more: Math.max(0, Number(data.more) || 0),
      test: data.test === true,
    };
  } catch {
    return null;
  }
}

function sender(m: PushMessage): string {
  return m.from || m.addr || "Unknown sender";
}

/** The notifications to show for a push: one per message when there are few, else one for all of them. A push must
 * always show something (Safari withdraws the subscription of a site that does not), so one that cannot be read
 * still says that there is new mail. */
export function notificationsFor(payload: PushPayload | null): ShownNotification[] {
  const base = { icon: ICON, badge: BADGE };
  if (payload?.test) {
    return [{ title: "Notifications work", options: { ...base, body: "New mail in the chosen folders will show like this.", tag: "test", data: { url: "/notifications" } } }];
  }
  const messages = payload?.messages ?? [];
  const total = messages.length + (payload?.more ?? 0);
  if (total === 0) return [{ title: "New mail", options: { ...base, tag: "new-mail", data: { url: "/" } } }];
  if (payload!.more === 0 && messages.length <= SEPARATE_MAX) {
    return messages.map((m) => ({
      title: sender(m),
      options: { ...base, body: [m.subject || "(no subject)", m.snippet].filter(Boolean).join("\n"), tag: m.id, data: { url: messageUrl(m.folder, m.id) } },
    }));
  }
  const folders = Array.from(new Set(messages.map((m) => m.folder)));
  const lines = messages.map((m) => `${sender(m)}: ${m.subject || "(no subject)"}`);
  if (payload!.more) lines.push(`and ${payload!.more} more`);
  return [
    {
      title: `${total} new messages`,
      // A tag of its own, so a later summary alerts again instead of replacing this one silently.
      options: { ...base, body: lines.join("\n"), tag: `new-mail:${messages[0]?.id ?? total}`, data: { url: folders.length === 1 ? folderUrl(folders[0]) : "/" } },
    },
  ];
}

// The place the last tapped notification opens, kept in Cache Storage (shared by the service worker and the app). On
// the iPhone neither way the worker tells the app is to be relied on: the message to an app coming back from the
// background can be lost, and an app started by the tap can start at its start page instead (WebKit bugs 252544,
// 263687). So the worker keeps the place here as well and the app looks for it when it starts or comes to the front.
const TAP_CACHE = "notification-tap";
const TAP_KEY = "/__notification-tap";
/** A tap older than this is not followed: the app was started or brought to the front for another reason. */
export const TAP_FRESH_MS = 60_000;

export async function rememberTap(url: string, now = Date.now()): Promise<void> {
  const cache = await caches.open(TAP_CACHE);
  await cache.put(TAP_KEY, new Response(JSON.stringify({ url, at: now }), { headers: { "Content-Type": "application/json" } }));
}

/** The place of a tap not followed yet, taken so it is followed once; null when there is none from the last minute. */
export async function takeTap(now = Date.now()): Promise<string | null> {
  if (typeof caches === "undefined") return null;
  const cache = await caches.open(TAP_CACHE);
  const kept = await cache.match(TAP_KEY);
  if (!kept) return null;
  const text = await kept.text();
  if (!(await cache.delete(TAP_KEY))) return null; // taken by another look at the same time
  try {
    const { url, at } = JSON.parse(text) as { url?: unknown; at?: unknown };
    return typeof url === "string" && typeof at === "number" && Math.abs(now - at) < TAP_FRESH_MS ? url : null;
  } catch {
    return null;
  }
}

/** What the app calls this device in the list of devices notified, e.g. "iPhone · Safari". */
export function deviceName(userAgent: string, brave = false, touchPoints = 0): string {
  const ua = userAgent;
  const device = /iPhone|iPod/.test(ua)
    ? "iPhone"
    : /iPad/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1)
      ? "iPad"
      : /Android/.test(ua)
        ? "Android"
        : /Windows/.test(ua)
          ? "Windows"
          : /Macintosh|Mac OS X/.test(ua)
            ? "Mac"
            : /CrOS/.test(ua)
              ? "ChromeOS"
              : /Linux/.test(ua)
                ? "Linux"
                : "Device";
  const browser = brave
    ? "Brave"
    : /Edg(e|A|iOS)?\//.test(ua)
      ? "Edge"
      : /SamsungBrowser\//.test(ua)
        ? "Samsung Internet"
        : /Firefox\/|FxiOS\//.test(ua)
          ? "Firefox"
          : /OPR\//.test(ua)
            ? "Opera"
            : /Chrome\/|CriOS\//.test(ua)
              ? "Chrome"
              : /Safari\//.test(ua) || /iPhone|iPad|Macintosh/.test(ua)
                ? "Safari"
                : null;
  return browser ? `${device} · ${browser}` : device;
}

/** base64url (the server's public key) to bytes, for pushManager.subscribe. */
export function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  const b64 = text.replace(/-/g, "+").replace(/_/g, "/").replace(/=+$/, "");
  const binary = atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

/** The server's name for a device: the first 16 hex digits of its endpoint's SHA-256 (yahoomail/push.py device_id). */
export async function deviceId(endpoint: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(endpoint));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("").slice(0, 16);
}
